import { useState, useRef, useEffect, useCallback } from 'react';
import {
  Play, Pause, RotateCcw, RotateCw, Volume2, VolumeX, Maximize2, Minimize2,
  ThumbsUp, ThumbsDown, Share2, Search, MessageSquare, CheckCircle2, UserCheck, PlaySquare, RefreshCw
} from 'lucide-react';
import { LongVideo, MediaHistoryItem, AppUser } from '../../types';
import { InstagramComments } from '../shared/components/InstagramComments';
import { ClueBadge } from '../shared/components/ClueBadge';
import {
  incrementMediaCount,
  followUser,
  unfollowUser,
  subscribeToFansCount,
  subscribeToFollowStatus,
  toggleMediaReaction,
  type MediaReaction,
  formatCount
} from '../../backend/mediaService';
import { shareContent } from '../../backend/sharingService';
import { ContentActionsMenu } from '../shared/components/ContentActionsMenu';
import { subscribeToBlockedUsers } from '../../backend/moderationService';
import { collection, doc, query, limit, getDocs, startAfter, QueryDocumentSnapshot, orderBy, where, onSnapshot } from 'firebase/firestore';
import { db } from '../../backend/firebase';
import { useMinuteClock } from '../../hooks/useMinuteClock';
import { formatRelativeTime } from '../../utils/relativeTime';
import { recordReadWriteCount } from '../../utils/withReadCounter';
import { PullToRefresh } from '../shared/components/PullToRefresh';
import { useLongsFeed } from '../../hooks/useLongsFeed';

interface LongsTabProps {
  mediaHistory?: MediaHistoryItem[];
  currentUser?: AppUser | null;
  onOpenAuth?: () => void;
  onGoLive?: () => void;
  highlightedMedia?: MediaHistoryItem;
  onOpenProfile?: (uid: string) => void;
  onToggleRightSidebar?: () => void;
}

export function LongsTab({ currentUser, onOpenAuth, highlightedMedia, onOpenProfile, onToggleRightSidebar }: LongsTabProps) {
  const relativeTimeNow = useMinuteClock();
  const [blockedUserIds, setBlockedUserIds] = useState<Set<string>>(new Set());
  const [blockedUsersReady, setBlockedUsersReady] = useState(!currentUser?.uid);
  const [blockedUsersError, setBlockedUsersError] = useState('');
  const {
    videos: feedVideos,
    isLoading,
    loadingMore,
    hasMore,
    error: feedError,
    newVideos,
    loadMore,
    refresh,
    prependNewVideos,
    retry,
    removeVideo
  } = useLongsFeed({ currentUserId: currentUser?.uid, blockedUserIds });

  // Selected Video Player state
  const [selectedVideo, setSelectedVideo] = useState<LongVideo | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [videoDuration, setVideoDuration] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const [volume, setVolume] = useState(1);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [controlsVisible, setControlsVisible] = useState(true);

  // Channel specific "More from this channel" state
  const [channelVideos, setChannelVideos] = useState<LongVideo[]>([]);
  const [loadingChannelMore, setLoadingChannelMore] = useState(false);
  const [channelHasMore, setChannelHasMore] = useState(true);
  const [channelLastDoc, setChannelLastDoc] = useState<QueryDocumentSnapshot | null>(null);

  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('All');
  const categories = ['All', 'Gaming', 'Tech', 'Music', 'Education', 'Podcasts', 'Live', 'Trending'];
  const [selectedEngagement, setSelectedEngagement] = useState({ likes: 0, dislikes: 0, comments: 0 });
  const [userReaction, setUserReaction] = useState<MediaReaction | null>(null);
  const [reactionBusy, setReactionBusy] = useState(false);
  const [reactionError, setReactionError] = useState('');
  const [selectedFollowerCount, setSelectedFollowerCount] = useState(0);
  const [showComments, setShowComments] = useState(false);
  const [isFollowing, setIsFollowing] = useState(false);
  const [followBusy, setFollowBusy] = useState(false);
  const [playedHistory, setPlayedHistory] = useState<string[]>([]);

  const videoRef = useRef<HTMLVideoElement>(null);
  const playerSectionRef = useRef<HTMLDivElement>(null);
  const feedSentinelRef = useRef<HTMLDivElement>(null);
  const scrollRootRef = useRef<HTMLDivElement | null>(null);
  const hideTimerRef = useRef<any>(null);
  const viewCounted = useRef(false);

  useEffect(() => {
    scrollRootRef.current = document.querySelector('[data-screen-scroll-root]');
  }, []);

  useEffect(() => {
    if (!currentUser?.uid) {
      setBlockedUserIds(new Set());
      setBlockedUsersReady(true);
      return;
    }
    setBlockedUsersReady(false);
    setBlockedUsersError('');
    return subscribeToBlockedUsers(currentUser.uid, blocked => {
      setBlockedUserIds(blocked);
      setBlockedUsersReady(true);
    }, error => {
      setBlockedUsersError(error.message);
      setBlockedUsersReady(true);
    });
  }, [currentUser?.uid]);

  // Set initial selected video only if highlightedMedia is explicitly provided
  useEffect(() => {
    if (highlightedMedia && (highlightedMedia.type === 'long' || highlightedMedia.type === 'video') && feedVideos.length > 0 && !selectedVideo) {
      const highlighted = feedVideos.find(v => v.id === highlightedMedia.id);
      if (highlighted) setSelectedVideo(highlighted);
    }
  }, [feedVideos, highlightedMedia?.id]);

  // PART D: Fetch "More from this channel" when selectedVideo changes
  const fetchChannelVideos = async (uploaderId: string, currentVidId: string, isRefresh = false) => {
    if (!uploaderId) {
      setChannelVideos([]);
      return;
    }
    try {
      if (isRefresh) {
        setChannelLastDoc(null);
        setChannelHasMore(true);
      }
      setLoadingChannelMore(true);

      const colRef = collection(db, 'media');
      // Query by uploaderId / ownerUid
      let q = query(
        colRef,
        where('ownerUid', '==', uploaderId),
        limit(30)
      );
      if (!isRefresh && channelLastDoc) {
        q = query(
          colRef,
          where('ownerUid', '==', uploaderId),
          startAfter(channelLastDoc),
          limit(30)
        );
      }

      const snapshot = await getDocs(q);
      const docs = snapshot.docs;
      if (docs.length > 0) {
        setChannelLastDoc(docs[docs.length - 1]);
      }
      setChannelHasMore(docs.length >= 30);

      const channelList: LongVideo[] = docs.map(docSnap => {
        const item = docSnap.data();
        return {
          id: docSnap.id,
          ownerUid: item.ownerUid || item.uploaderId,
          clueId: item.clueId,
          title: item.title || 'Untitled Video',
          description: item.caption || item.description || '',
          authorName: item.authorName || selectedVideo?.authorName || 'Creator',
          authorUsername: item.authorUsername || selectedVideo?.authorUsername || 'creator',
          authorAvatar: item.authorAvatar || selectedVideo?.authorAvatar || '',
          videoUrl: item.mediaUrl || item.storagePath,
          thumbnailUrl: item.thumbnailUrl || item.mediaUrl || 'https://images.unsplash.com/photo-1611162617474-5b21e879e113?w=720',
          duration: item.duration || '10:00',
          views: formatCount(item.viewCount || item.views || 0) + ' views',
          uploadedAt: formatRelativeTime(item.createdAt, relativeTimeNow),
          createdAt: item.createdAt,
          followers: formatCount(item.followerCount || item.fansCount || 0),
          likes: Number(item.likeCount ?? item.likes ?? 0),
          dislikes: Number(item.dislikeCount ?? item.dislikes ?? 0),
          comments: Number(item.commentCount ?? item.commentsCount ?? 0),
          isFollowing: false
        };
      }).filter(v => v.id !== currentVidId && v.videoUrl);

      // Fallback from main feed if user has no other distinct Firestore media docs
      const fallbackChannelVids = feedVideos.filter(v => v.ownerUid === uploaderId && v.id !== currentVidId);

      const map = new Map<string, LongVideo>();
      [...channelList, ...fallbackChannelVids].forEach(v => {
        if (v && v.id && !map.has(v.id)) map.set(v.id, v);
      });

      setChannelVideos(Array.from(map.values()));
      setLoadingChannelMore(false);
    } catch (err) {
      console.warn('Channel videos fetch warning (falling back to feed filter):', err);
      const fallback = feedVideos.filter(v => v.ownerUid === uploaderId && v.id !== currentVidId);
      setChannelVideos(fallback);
      setLoadingChannelMore(false);
    }
  };

  useEffect(() => {
    if (selectedVideo) {
      void fetchChannelVideos(selectedVideo.ownerUid || '', selectedVideo.id, true);
    }
  }, [selectedVideo?.id]);

  // View counter algorithm
  useEffect(() => {
    let timer: any;
    if (isPlaying && selectedVideo) {
      if (!viewCounted.current) {
        timer = setTimeout(() => {
          incrementMediaCount(selectedVideo.id, 'viewCount');
          viewCounted.current = true;
        }, 5000);
      }
    }
    return () => clearTimeout(timer);
  }, [isPlaying, selectedVideo?.id]);

  useEffect(() => {
    viewCounted.current = false;
  }, [selectedVideo?.id]);

  useEffect(() => {
    if (isLoading || loadingMore || !hasMore || !blockedUsersReady) return;
    const sentinel = feedSentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(entries => {
      if (entries[0]?.isIntersecting && hasMore) {
        void loadMore();
      }
    }, { root: scrollRootRef.current, rootMargin: '700px' });
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [blockedUsersReady, feedVideos.length, hasMore, isLoading, loadMore, loadingMore]);

  // Follow status
  useEffect(() => {
    if (!currentUser || !selectedVideo?.ownerUid) return;
    return subscribeToFollowStatus(currentUser.uid, selectedVideo.ownerUid, setIsFollowing);
  }, [currentUser?.uid, selectedVideo?.ownerUid]);

  useEffect(() => {
    if (!selectedVideo) return;
    setSelectedEngagement({
      likes: selectedVideo.likes || 0,
      dislikes: selectedVideo.dislikes || 0,
      comments: selectedVideo.comments || 0
    });
    setReactionError('');
    return onSnapshot(doc(db, 'media', selectedVideo.id), snapshot => {
      recordReadWriteCount('longs-engagement', { reads: snapshot.metadata.fromCache ? 0 : 1 });
      if (!snapshot.exists()) return;
      setSelectedEngagement({
        likes: Number(snapshot.get('likeCount') ?? snapshot.get('likesCount') ?? selectedVideo.likes ?? 0),
        dislikes: Number(snapshot.get('dislikeCount') ?? selectedVideo.dislikes ?? 0),
        comments: Number(snapshot.get('commentCount') ?? snapshot.get('commentsCount') ?? selectedVideo.comments ?? 0)
      });
    }, error => {
      console.error('Could not subscribe to Longs engagement counts:', error);
      setReactionError('Could not load the latest video counts.');
    });
  }, [selectedVideo?.id]);

  useEffect(() => {
    if (!selectedVideo || !currentUser?.uid) {
      setUserReaction(null);
      return;
    }
    return onSnapshot(
      doc(db, 'media', selectedVideo.id, 'likes', currentUser.uid),
      snapshot => {
        recordReadWriteCount('longs-reaction-state', { reads: snapshot.metadata.fromCache ? 0 : 1 });
        setUserReaction(snapshot.exists()
          ? (snapshot.get('reaction') === 'dislike' ? 'dislike' : 'like')
          : null);
      },
      error => {
        console.error('Could not load the Longs reaction state:', error);
        setReactionError('Could not load your reaction state.');
      }
    );
  }, [currentUser?.uid, selectedVideo?.id]);

  useEffect(() => {
    if (!selectedVideo?.ownerUid) {
      setSelectedFollowerCount(0);
      return;
    }
    setSelectedFollowerCount(0);
    return subscribeToFansCount(selectedVideo.ownerUid, setSelectedFollowerCount, error => {
      console.error('Could not load Longs creator follower count:', error);
      setReactionError('Could not load the creator follower count.');
    });
  }, [selectedVideo?.ownerUid]);

  const handleFollowClick = async () => {
    if (!currentUser) {
      onOpenAuth?.();
      return;
    }
    if (!selectedVideo?.ownerUid || followBusy) return;
    const wasFollowing = isFollowing;
    setIsFollowing(!wasFollowing);
    setFollowBusy(true);
    try {
      if (wasFollowing) await unfollowUser(selectedVideo.ownerUid, currentUser);
      else await followUser(selectedVideo.ownerUid, currentUser, selectedVideo.authorName);
    } catch (error) {
      console.error('Could not update long-video follow state:', error);
      setIsFollowing(wasFollowing);
    } finally {
      setFollowBusy(false);
    }
  };

  // Auto-hide controls timer
  const resetControlsTimer = useCallback(() => {
    setControlsVisible(true);
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    hideTimerRef.current = setTimeout(() => {
      if (isPlaying) {
        setControlsVisible(false);
      }
    }, 3000);
  }, [isPlaying]);

  useEffect(() => {
    if (isPlaying) {
      resetControlsTimer();
    } else {
      setControlsVisible(true);
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    }
  }, [isPlaying, resetControlsTimer]);

  const togglePlay = () => {
    if (!videoRef.current) return;
    if (videoRef.current.paused) {
      void videoRef.current.play();
      setIsPlaying(true);
    } else {
      videoRef.current.pause();
      setIsPlaying(false);
    }
    resetControlsTimer();
  };

  const skipTime = (seconds: number) => {
    if (!videoRef.current) return;
    videoRef.current.currentTime = Math.max(0, Math.min(videoRef.current.duration || 0, videoRef.current.currentTime + seconds));
    resetControlsTimer();
  };

  // PART E: AUTOPLAY NEXT (strictly within same channel's list)
  const handleVideoEnded = () => {
    if (!selectedVideo) return;
    setPlayedHistory(prev => [...prev, selectedVideo.id]);
    const remainingChannelVids = channelVideos.filter(v => !playedHistory.includes(v.id) && v.id !== selectedVideo.id);
    const nextVid = remainingChannelVids.length > 0 ? remainingChannelVids[0] : channelVideos.find(v => v.id !== selectedVideo.id);

    if (nextVid) {
      setSelectedVideo(nextVid);
      setIsPlaying(true);
    } else {
      setIsPlaying(false); // Stop playback and show replay option if no more channel videos
    }
  };

  const toggleReaction = async (reaction: MediaReaction) => {
    if (!currentUser) {
      onOpenAuth?.();
      return;
    }
    if (!selectedVideo || reactionBusy) return;
    setReactionBusy(true);
    setReactionError('');
    try {
      const nextReaction = await toggleMediaReaction(selectedVideo.id, currentUser.uid, reaction);
      setSelectedEngagement(previous => {
        const counts = { ...previous };
        if (userReaction) counts[userReaction === 'like' ? 'likes' : 'dislikes'] = Math.max(0, counts[userReaction === 'like' ? 'likes' : 'dislikes'] - 1);
        if (nextReaction) counts[nextReaction === 'like' ? 'likes' : 'dislikes'] += 1;
        return counts;
      });
      setUserReaction(nextReaction);
    } catch (error) {
      console.error('Could not update Longs reaction:', error);
      setReactionError(error instanceof Error ? error.message : 'Could not update your reaction.');
    } finally {
      setReactionBusy(false);
    }
  };

  const filteredFeedVideos = feedVideos.filter(v => {
    if (!v) return false;
    if (v.ownerUid && blockedUserIds.has(v.ownerUid)) return false;
    const title = (v.title || '').toLowerCase();
    const author = (v.authorName || '').toLowerCase();
    const query = (searchQuery || '').toLowerCase();
    return title.includes(query) || author.includes(query);
  });

  useEffect(() => {
    if (!selectedVideo?.ownerUid || !blockedUserIds.has(selectedVideo.ownerUid)) return;
    setSelectedVideo(feedVideos.find(video => !video.ownerUid || !blockedUserIds.has(video.ownerUid)) || null);
  }, [blockedUserIds, feedVideos, selectedVideo?.id]);

  const handleContentDeleted = (id: string) => {
    const remaining = feedVideos.filter(video => video.id !== id);
    removeVideo(id);
    setChannelVideos(previous => previous.filter(video => video.id !== id));
    if (selectedVideo?.id === id) setSelectedVideo(remaining[0] || null);
  };

  const selectVideo = (video: LongVideo) => {
    setSelectedVideo(video);
    setIsPlaying(true);
    requestAnimationFrame(() => playerSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
  };

  const isSelfVideo = currentUser && selectedVideo?.ownerUid === currentUser.uid;

  if (blockedUsersError) return <div role="alert" className="p-6 text-center text-sm text-rose-700">Could not load your block list. Longs content is hidden until it is available.</div>;

  const refreshFeed = async () => {
    await refresh((scrollRootRef.current?.scrollTop || 0) < 40);
  };

  const showNewVideos = () => {
    prependNewVideos();
    scrollRootRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div
      id="longs-view-container"
      className={`flex w-full flex-col bg-white text-slate-900 dark:bg-slate-950 dark:text-slate-100 ${isFullscreen ? 'fixed inset-0 z-50 overflow-y-auto bg-black pb-0' : 'pb-[calc(5rem+var(--sab))]'}`}
    >
      {/* PART D: VIDEO PLAYER SCREEN (Only shown when a video is selected) */}
      {selectedVideo && (
        <div className="border-b-4 border-slate-100">
          {!isFullscreen && (
            <div className="p-3 bg-slate-900 text-white border-b border-slate-800 flex items-center justify-between">
              <button
                onClick={() => {
                  setSelectedVideo(null);
                  setIsPlaying(false);
                }}
                className="inline-flex items-center gap-1.5 text-xs font-bold text-slate-200 hover:text-white px-3 py-1.5 rounded-xl bg-slate-800 transition-colors"
              >
                ← Back to Feed
              </button>
              <span className="text-xs font-bold truncate max-w-[60%] text-slate-300">{selectedVideo.title}</span>
              <div />
            </div>
          )}

          <div ref={playerSectionRef} className={`relative bg-black text-white select-none ${isFullscreen ? 'h-screen w-screen flex items-center justify-center' : 'w-full'}`}>
        <div
          className={`relative w-full bg-slate-950 flex items-center justify-center overflow-hidden transition-all duration-300 ${
            isFullscreen ? 'h-full w-full' : 'aspect-video max-h-[320px] sm:max-h-[380px]'
          }`}
          onMouseMove={resetControlsTimer}
          onTouchStart={resetControlsTimer}
        >
          {selectedVideo.videoUrl ? (
            <video
              ref={videoRef}
              src={selectedVideo.videoUrl}
              playsInline
              autoPlay={isPlaying}
              muted={isMuted}
              onTimeUpdate={() => videoRef.current && setCurrentTime(videoRef.current.currentTime)}
              onLoadedMetadata={() => videoRef.current && setVideoDuration(videoRef.current.duration)}
              onEnded={handleVideoEnded}
              className="w-full h-full object-contain bg-black cursor-pointer"
              onClick={togglePlay}
            />
          ) : (
            <img
              src={selectedVideo.thumbnailUrl}
              alt={selectedVideo.title}
              className="w-full h-full object-cover opacity-90 cursor-pointer"
              onClick={togglePlay}
            />
          )}

          {/* PART D: Transparent Overlay Player Controls */}
          <div
            className={`absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-black/60 flex flex-col justify-between p-4 transition-opacity duration-300 ${
              controlsVisible ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
            }`}
          >
            <div className="flex items-center justify-between">
              {isFullscreen ? (
                <h3 className="font-bold text-sm sm:text-base text-white truncate max-w-[80%] drop-shadow-md">
                  {selectedVideo.title}
                </h3>
              ) : <div />}
              <div className="flex items-center gap-2">
                {isFullscreen && (
                  <button
                    onClick={() => setIsFullscreen(false)}
                    className="p-2 rounded-xl bg-black/50 text-white backdrop-blur-md hover:bg-black/80 transition-all"
                  >
                    <Minimize2 className="w-5 h-5 text-white" />
                  </button>
                )}
              </div>
            </div>

            <div className="flex items-center justify-center gap-6 sm:gap-10">
              <button
                onClick={() => skipTime(-7)}
                className="p-3 rounded-full bg-black/40 backdrop-blur-md text-white hover:bg-black/70 transition-transform active:scale-95"
                title="7 seconds backward"
              >
                <RotateCcw className="w-6 h-6 sm:w-7 sm:h-7" />
              </button>

              <button
                onClick={togglePlay}
                className="p-4 rounded-full bg-blue-600/90 hover:bg-blue-600 text-white shadow-2xl backdrop-blur-md transition-transform active:scale-95"
              >
                {isPlaying ? <Pause className="w-8 h-8 sm:w-10 sm:h-10" /> : <Play className="w-8 h-8 sm:w-10 sm:h-10 fill-white" />}
              </button>

              <button
                onClick={() => skipTime(7)}
                className="p-3 rounded-full bg-black/40 backdrop-blur-md text-white hover:bg-black/70 transition-transform active:scale-95"
                title="7 seconds forward"
              >
                <RotateCw className="w-6 h-6 sm:w-7 sm:h-7" />
              </button>
            </div>

            <div className="space-y-2">
              <div className="flex items-center gap-3">
                <span className="text-[10px] font-bold text-white/90">
                  {Math.floor(currentTime / 60)}:{Math.floor(currentTime % 60).toString().padStart(2, '0')}
                </span>
                <input
                  type="range"
                  min={0}
                  max={videoDuration || 100}
                  value={currentTime}
                  onChange={e => {
                    if (videoRef.current) {
                      videoRef.current.currentTime = Number(e.target.value);
                      setCurrentTime(Number(e.target.value));
                    }
                  }}
                  className="flex-1 h-1.5 bg-white/30 rounded-lg appearance-none cursor-pointer accent-blue-600"
                />
                <span className="text-[10px] font-bold text-white/90">
                  {Math.floor(videoDuration / 60)}:{Math.floor(videoDuration % 60).toString().padStart(2, '0')}
                </span>
              </div>

              <div className="flex items-center justify-between">
                {!isFullscreen ? (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => {
                        const newMuted = !isMuted;
                        setIsMuted(newMuted);
                        if (videoRef.current) videoRef.current.muted = newMuted;
                      }}
                      className="p-2 rounded-lg bg-black/40 text-white hover:bg-black/70 backdrop-blur-md transition-all"
                    >
                      {isMuted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
                    </button>
                    <input
                      type="range"
                      min={0}
                      max={1}
                      step={0.05}
                      value={isMuted ? 0 : volume}
                      onChange={e => {
                        const val = Number(e.target.value);
                        setVolume(val);
                        setIsMuted(val === 0);
                        if (videoRef.current) {
                          videoRef.current.volume = val;
                          videoRef.current.muted = val === 0;
                        }
                      }}
                      className="w-20 h-1 bg-white/30 rounded-lg appearance-none cursor-pointer accent-blue-600"
                    />
                  </div>
                ) : <div />}

                <button
                  onClick={() => setIsFullscreen(!isFullscreen)}
                  className="p-2 rounded-lg bg-black/40 text-white hover:bg-black/70 backdrop-blur-md transition-all ml-auto"
                >
                  {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Video Metadata & Creator Info (Below player) */}
        {!isFullscreen && (
          <div className="p-4 bg-white text-slate-900 border-b border-slate-100 space-y-3">
            <h2 className="font-black text-sm sm:text-base leading-tight text-slate-900 line-clamp-2 uppercase tracking-tighter">
              {selectedVideo.title}
            </h2>

            <div className="flex items-center gap-3 text-[11px] text-slate-400 font-bold uppercase tracking-widest">
              <span>{selectedVideo.views}</span>
              <div className="w-1 h-1 rounded-full bg-slate-200" />
              <span>{formatRelativeTime(selectedVideo.createdAt, relativeTimeNow)}</span>
            </div>

            <div className="flex items-center justify-between pt-3 border-t border-slate-50">
              <div className="flex items-center gap-3">
                <img
                  src={selectedVideo.authorAvatar}
                  alt={selectedVideo.authorName}
                  className="w-10 h-10 rounded-2xl object-cover border border-slate-100 shadow-sm cursor-pointer"
                  onClick={() => selectedVideo.ownerUid && onOpenProfile?.(selectedVideo.ownerUid)}
                />
                <div onClick={() => selectedVideo.ownerUid && onOpenProfile?.(selectedVideo.ownerUid)} className="cursor-pointer group">
                  <div className="flex items-center gap-1.5 font-black text-xs text-slate-900 uppercase tracking-tighter">
                    <span className="group-hover:text-blue-900">{selectedVideo.authorName}</span>
                    <ClueBadge id={selectedVideo.clueId} />
                    <CheckCircle2 className="w-3.5 h-3.5 text-blue-600 fill-blue-600" />
                  </div>
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                    {formatCount(selectedFollowerCount)} Followers
                  </span>
                </div>
              </div>

              {!isSelfVideo && (
                <button
                  onClick={() => void handleFollowClick()}
                  disabled={followBusy}
                  className={`px-5 py-2 rounded-xl text-[11px] font-black uppercase tracking-widest transition-all shadow-md ${
                    isFollowing
                      ? 'bg-slate-100 text-slate-500 border border-slate-200 cursor-default'
                      : 'bg-blue-900 text-white shadow-blue-900/20 active:scale-95'
                  }`}
                >
                  {isFollowing ? <><UserCheck className="inline w-3 h-3 mr-1" />Following</> : 'Follow'}
                </button>
              )}
            </div>

            {reactionError && <p role="alert" className="rounded-xl bg-rose-50 px-3 py-2 text-xs text-rose-700">{reactionError}</p>}

            {/* Action Bar (Horizontally scrollable) */}
            <div className="flex items-center gap-2 pt-2 overflow-x-auto no-scrollbar pb-1">
              <div className="flex items-center rounded-2xl bg-slate-50 text-slate-800 text-[11px] font-black border border-slate-100 shadow-inner shrink-0">
                <button
                  type="button"
                  aria-label="Like video"
                  aria-pressed={userReaction === 'like'}
                  disabled={reactionBusy}
                  onClick={() => void toggleReaction('like')}
                  className="flex items-center gap-2 px-3.5 py-2 hover:bg-slate-100 rounded-l-2xl transition-colors disabled:opacity-50"
                >
                  <ThumbsUp className={`w-4 h-4 ${userReaction === 'like' ? 'text-blue-900 fill-blue-900' : 'text-slate-500'}`} />
                  <span>{formatCount(selectedEngagement.likes)}</span>
                </button>
                <div className="h-4 w-px bg-slate-200" />
                <button
                  type="button"
                  aria-label="Dislike video"
                  aria-pressed={userReaction === 'dislike'}
                  disabled={reactionBusy}
                  onClick={() => void toggleReaction('dislike')}
                  className="flex items-center gap-2 px-3 py-2 hover:bg-slate-100 rounded-r-2xl transition-colors disabled:opacity-50"
                >
                  <ThumbsDown className={`w-4 h-4 ${userReaction === 'dislike' ? 'text-blue-900 fill-blue-900' : 'text-slate-500'}`} />
                  <span>{formatCount(selectedEngagement.dislikes)}</span>
                </button>
              </div>

              <button
                type="button"
                onClick={() => setShowComments(true)}
                className="flex items-center gap-2 px-4 py-2 rounded-2xl bg-slate-50 hover:bg-slate-100 text-slate-800 text-[11px] font-black border border-slate-100 shadow-inner transition-colors shrink-0"
              >
                <MessageSquare className="w-4 h-4 text-blue-900" />
                <span>{formatCount(selectedEngagement.comments)} Comments</span>
              </button>

              <button
                onClick={() => {
                  void shareContent('long', selectedVideo.id, selectedVideo.title)
                    .then(() => incrementMediaCount(selectedVideo.id, 'shareCount'))
                    .catch(err => { if (err?.name !== 'AbortError') console.error('Share failed:', err); });
                }}
                className="flex items-center gap-2 px-4 py-2 rounded-2xl bg-slate-50 text-slate-800 text-[11px] font-black hover:bg-slate-100 border border-slate-100 shadow-inner shrink-0"
              >
                <Share2 className="w-4 h-4 text-slate-500" />
                <span>Share</span>
              </button>
              <ContentActionsMenu
                contentId={selectedVideo.id}
                contentType="long"
                contentOwnerUid={selectedVideo.ownerUid}
                contentTitle={selectedVideo.title}
                currentUser={currentUser}
                onOpenAuth={onOpenAuth}
                onDeleted={handleContentDeleted}
              />
            </div>
          </div>
        )}
      </div>

      {/* PART D: "MORE FROM THIS CHANNEL" Section */}
      {!isFullscreen && (
        <div className="p-4 space-y-4 bg-slate-50/50 border-t border-slate-100">
          <div className="flex items-center justify-between">
            <h3 className="font-black text-xs text-slate-900 uppercase tracking-widest">
              More from {selectedVideo.authorName}
            </h3>
            <span className="text-[10px] text-slate-400 font-bold">{channelVideos.length} videos</span>
          </div>

          <div className="space-y-3">
            {channelVideos.filter(video => !video.ownerUid || !blockedUserIds.has(video.ownerUid)).map(video => (
              <div
                key={video.id}
                onClick={() => {
                  selectVideo(video);
                }}
                className="flex items-center gap-3 bg-white p-2.5 rounded-2xl border border-slate-100 shadow-2xs hover:shadow-md transition-all cursor-pointer group"
              >
                <div className="relative w-28 h-16 rounded-xl overflow-hidden bg-slate-900 shrink-0">
                  <img
                    src={video.thumbnailUrl}
                    alt={video.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                    loading="lazy"
                  />
                  <span className="absolute bottom-1 right-1 bg-black/80 px-1 py-0.5 rounded text-[8px] font-black text-white">
                    {video.duration}
                  </span>
                </div>
                <div className="flex flex-col justify-center min-w-0 flex-1">
                  <h4 className="font-black text-xs text-slate-900 line-clamp-2 leading-tight uppercase tracking-tighter">
                    {video.title}
                  </h4>
                  <div className="flex items-center gap-2 mt-1 text-[9px] text-slate-400 font-bold uppercase">
                    <span>@{video.authorUsername}</span>
                    <span>•</span>
                    <span>{video.views}</span>
                  </div>
                </div>
              </div>
            ))}

            {channelVideos.length === 0 && (
              <div className="py-6 text-center bg-white rounded-2xl border border-slate-100">
                <p className="text-xs text-slate-400 font-bold">No more videos from this channel</p>
              </div>
            )}
          </div>
        </div>
      )}
      </div>
      )}

      {/* Longs feed */}
      {!isFullscreen && (
        <div className="w-full border-t border-slate-100 bg-white dark:border-slate-800 dark:bg-slate-950">
          <div className="sticky top-0 z-20 border-b border-slate-200 bg-white/95 px-3 py-3 backdrop-blur dark:border-slate-800 dark:bg-slate-950/95 sm:px-5">
            <div className="mx-auto flex w-full max-w-[1100px] items-center justify-between gap-3">
              <h1 className="text-lg font-bold tracking-tight text-slate-900 dark:text-slate-100">Longs</h1>
              <div className="flex items-center gap-2">
                <div className="relative hidden w-64 sm:block">
                  <Search className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
                  <input
                    type="search"
                    value={searchQuery}
                    onChange={event => setSearchQuery(event.target.value)}
                    placeholder="Search Longs and channels"
                    className="w-full rounded-lg border border-slate-200 bg-slate-50 py-2 pl-9 pr-3 text-xs text-slate-900 outline-none focus:border-blue-600 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
                  />
                </div>
                <button
                  type="button"
                  onClick={() => void refreshFeed()}
                  className="hidden items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-900 sm:inline-flex"
                  aria-label="Refresh Longs"
                >
                  <RefreshCw className="h-3.5 w-3.5" />
                  Refresh
                </button>
                {onToggleRightSidebar && (
                  <button
                    type="button"
                    onClick={onToggleRightSidebar}
                    className="hidden rounded-lg border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 min-[1280px]:inline-flex dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-900"
                    aria-label="Toggle right sidebar"
                  >
                    Sidebar
                  </button>
                )}
              </div>
            </div>
            <div className="mx-auto mt-3 flex w-full max-w-[1100px] gap-2 overflow-x-auto whitespace-nowrap no-scrollbar">
              {categories.map(category => (
                <button
                  key={category}
                  type="button"
                  onClick={() => setActiveCategory(category)}
                  className={`shrink-0 rounded-lg px-3.5 py-2 text-xs font-semibold transition-colors ${
                    activeCategory === category
                      ? 'bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900'
                      : 'bg-slate-100 text-slate-700 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700'
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>
            <div className="relative mx-auto mt-3 w-full max-w-[1100px] sm:hidden">
              <Search className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
              <input
                type="search"
                value={searchQuery}
                onChange={event => setSearchQuery(event.target.value)}
                placeholder="Search Longs and channels"
                className="w-full rounded-lg border border-slate-200 bg-slate-50 py-2 pl-9 pr-3 text-xs text-slate-900 outline-none focus:border-blue-600 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
              />
            </div>
          </div>

          <PullToRefresh onRefresh={() => refreshFeed()} scrollRef={scrollRootRef}>
            <div className="mx-auto w-full max-w-[1100px] sm:px-4">
              {newVideos.length > 0 && (
                <div className="sticky top-[112px] z-10 flex justify-center py-2 sm:top-[68px]">
                  <button
                    type="button"
                    onClick={showNewVideos}
                    className="rounded-full bg-blue-700 px-4 py-2 text-xs font-semibold text-white shadow-lg hover:bg-blue-800"
                  >
                    New videos
                  </button>
                </div>
              )}
              {feedError && (
                <div role="alert" className="mx-3 my-4 flex items-center justify-between gap-3 rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800 dark:border-rose-900 dark:bg-rose-950 dark:text-rose-200 sm:mx-0">
                  <span>Could not load more videos.</span>
                  <button type="button" onClick={retry} className="shrink-0 rounded-lg bg-rose-700 px-3 py-1.5 text-xs font-bold text-white hover:bg-rose-800">Retry</button>
                </div>
              )}

              {(isLoading || !blockedUsersReady) && (
                <div className="space-y-5 px-3 py-4 sm:px-0" role="status" aria-label="Loading Longs">
                  {[0, 1, 2].map(row => (
                    <div key={row} className="flex flex-col gap-3 sm:flex-row">
                      <div className="aspect-video w-full animate-pulse rounded-xl bg-slate-200 dark:bg-slate-800 sm:w-[38%] sm:min-w-[240px] sm:max-w-[360px]" />
                      <div className="flex-1 space-y-3 py-1">
                        <div className="h-5 w-4/5 animate-pulse rounded bg-slate-200 dark:bg-slate-800" />
                        <div className="h-3 w-2/5 animate-pulse rounded bg-slate-200 dark:bg-slate-800" />
                        <div className="h-3 w-1/3 animate-pulse rounded bg-slate-200 dark:bg-slate-800" />
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {!isLoading && blockedUsersReady && feedVideos.length === 0 && !feedError && (
                <div className="flex min-h-[45vh] flex-col items-center justify-center px-6 text-center">
                  <PlaySquare className="mb-3 h-10 w-10 text-slate-400" />
                  <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">No Longs yet</h2>
                  <p className="mt-1 max-w-sm text-sm text-slate-500 dark:text-slate-400">Videos will appear here as creators publish them. Try refreshing in a little while.</p>
                  <button type="button" onClick={() => void refreshFeed()} className="mt-4 rounded-lg bg-slate-900 px-4 py-2 text-xs font-semibold text-white dark:bg-slate-100 dark:text-slate-900">Refresh</button>
                </div>
              )}

              {!isLoading && blockedUsersReady && feedVideos.length > 0 && filteredFeedVideos.filter(video =>
                activeCategory === 'All' || (video.title + video.description).toLowerCase().includes(activeCategory.toLowerCase())
              ).length === 0 && (
                <div className="px-6 py-16 text-center text-sm text-slate-500 dark:text-slate-400">
                  No videos match this search or category.
                </div>
              )}

              <div className="divide-y divide-slate-100 dark:divide-slate-800">
                {blockedUsersReady && filteredFeedVideos
                  .filter(video => activeCategory === 'All' || (video.title + video.description).toLowerCase().includes(activeCategory.toLowerCase()))
                  .map(video => (
                    <article
                      key={video.id}
                      role="button"
                      tabIndex={0}
                      onClick={() => selectVideo(video)}
                      onKeyDown={event => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          selectVideo(video);
                        }
                      }}
                      className="group flex cursor-pointer flex-col gap-2 py-3 sm:flex-row sm:gap-5 sm:py-5 [content-visibility:auto] [contain-intrinsic-size:300px]"
                    >
                      <div className="relative aspect-video w-full shrink-0 overflow-hidden bg-slate-950 sm:w-[38%] sm:min-w-[240px] sm:max-w-[360px] sm:rounded-xl">
                        <img
                          src={video.thumbnailUrl}
                          alt={video.title}
                          loading="lazy"
                          className="h-full w-full object-cover transition-transform duration-200 group-hover:scale-[1.02]"
                        />
                        {video.duration && (
                          <span className="absolute bottom-2 right-2 rounded bg-black/85 px-1.5 py-0.5 text-[10px] font-semibold text-white">
                            {video.duration}
                          </span>
                        )}
                        <div className="absolute right-2 top-2 z-10 opacity-100 sm:opacity-0 sm:transition-opacity sm:group-hover:opacity-100" onClick={event => event.stopPropagation()}>
                          <ContentActionsMenu
                            contentId={video.id}
                            contentType="long"
                            contentOwnerUid={video.ownerUid}
                            contentTitle={video.title}
                            currentUser={currentUser}
                            onOpenAuth={onOpenAuth}
                            onDeleted={handleContentDeleted}
                          />
                        </div>
                      </div>
                      <div className="flex min-w-0 flex-1 items-start gap-3 px-3 sm:block sm:px-0">
                        <img
                          src={video.authorAvatar}
                          alt=""
                          loading="lazy"
                          className="mt-0.5 h-9 w-9 shrink-0 rounded-full object-cover sm:hidden"
                        />
                        <div className="min-w-0 flex-1">
                          <h2 className="line-clamp-2 text-sm font-semibold leading-5 text-slate-900 group-hover:text-blue-700 dark:text-slate-100 dark:group-hover:text-blue-300 sm:text-base sm:leading-6">
                            {video.title}
                          </h2>
                          <p className="mt-1 hidden items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400 sm:flex">
                            <span>{video.views}</span>
                            {video.uploadedAt && <><span aria-hidden="true">·</span><span>{video.uploadedAt}</span></>}
                          </p>
                          <div className="mt-2 hidden min-w-0 items-center gap-2 sm:flex">
                            <img src={video.authorAvatar} alt="" loading="lazy" className="h-7 w-7 shrink-0 rounded-full object-cover" />
                            <span className="truncate text-xs font-medium text-slate-700 dark:text-slate-300">{video.authorName}</span>
                            <CheckCircle2 className="h-3.5 w-3.5 shrink-0 fill-blue-600 text-blue-600" aria-label="Verified channel" />
                          </div>
                          <p className="mt-1 line-clamp-1 text-[11px] text-slate-500 dark:text-slate-400 sm:hidden">
                            {video.authorName} · {video.views}{video.uploadedAt ? ` · ${video.uploadedAt}` : ''}
                          </p>
                          {video.description && (
                            <p className="mt-2 hidden line-clamp-2 text-xs leading-5 text-slate-500 dark:text-slate-400 sm:block">
                              {video.description}
                            </p>
                          )}
                        </div>
                      </div>
                    </article>
                  ))}
              </div>

              {loadingMore && (
                <div className="space-y-5 px-3 py-4 sm:px-0" role="status" aria-label="Loading more Longs">
                  {[0, 1].map(row => (
                    <div key={row} className="flex flex-col gap-3 sm:flex-row">
                      <div className="aspect-video w-full animate-pulse rounded-xl bg-slate-200 dark:bg-slate-800 sm:w-[38%] sm:min-w-[240px] sm:max-w-[360px]" />
                      <div className="flex-1 space-y-3 py-1">
                        <div className="h-5 w-4/5 animate-pulse rounded bg-slate-200 dark:bg-slate-800" />
                        <div className="h-3 w-2/5 animate-pulse rounded bg-slate-200 dark:bg-slate-800" />
                        <div className="h-3 w-1/3 animate-pulse rounded bg-slate-200 dark:bg-slate-800" />
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <div ref={feedSentinelRef} aria-hidden="true" className="h-px" />
              {!hasMore && feedVideos.length > 0 && (
                <div className="py-8 text-center text-xs font-medium text-slate-500 dark:text-slate-400">
                  You're all caught up
                </div>
              )}
            </div>
          </PullToRefresh>
        </div>
      )}

      {/* Comments Drawer (Only when a video is selected) */}
      {selectedVideo && (
        <InstagramComments
          isOpen={showComments}
          onClose={() => setShowComments(false)}
          mediaId={selectedVideo.id}
          mediaType="long"
          currentUser={currentUser || null}
          onOpenAuth={onOpenAuth || (() => {})}
          authorName={selectedVideo.authorName}
          likesCount={selectedEngagement.likes}
          isMediaLiked={userReaction === 'like'}
          onLikeToggle={() => void toggleReaction('like')}
          timeAgo={formatRelativeTime(selectedVideo.createdAt, relativeTimeNow)}
          onOpenProfile={onOpenProfile}
        />
      )}
    </div>
  );
}
